import { SnackbarProvider } from "notistack";
import React from "react";
import { IntlProvider } from "react-intl";
import { Provider as ReduxProvider } from "react-redux";
import { BrowserRouter, Redirect, Route, Router, Switch } from "react-router-dom";
import "./assets/font/flaticon.css";
import {
  ConfigThemeProvider,
  FeatureProvider,
  FeatureRoute,
  GoogleTagsProvider,
  PrivateRoute
} from "./components";
import DataLoader from "./components/DataLoader";
import config from "./config";
import { configureStore } from "./ducks/store";
import { useCongress, useUserMe } from "./hooks";
import { translations } from "./i18n";
import {
  AccessCodePage,
  AgendaPage,
  PhysicalRoomsPage,
  CountDownPage,
  EPosterPage,
  EPostersPage,
  ExhibitorPage,
  ExhibitorsPage,
  ExpertPage,
  ExpertsPage,
  FavouritesPage,
  ForgotPasswordPage,
  ForumPage,
  ForumTopicPage,
  HelpDeskPage,
  HomePage,
  LearnPage,
  LoginPage,
  MeetingPage,
  MeetingSchedulerPage,
  MyBadgePage,
  MyPostersPage,
  MyPresentationsPage,
  PhysicalRoomPage,
  ProfilePage,
  ProgramPage,
  RegisterPage,
  ResetPasswordPage,
  SessionPage,
  SessionsPage,
  SocialWallPage,
  TermsPage,
  ThankYouPage,
  QrScanPage,
  InfoPage
} from "./pages";

// numeral.register("locale", "it", {
//   delimiters: {
//     thousands: " ",
//     decimal: ","
//   },
//   currency: {
//     symbol: "€"
//   }
// });

const { language } = config;

function App({ history }) {
  const RouterInstance = config.appEnv === "test" ? Router : BrowserRouter;
  const store = configureStore({});

  // const gtmParams = {
  //   id: "GTM-MN37F67",
  //   dataLayer: {
  //     congress: window.location.hostname
  //   }
  // };

  const Routes = () => {
    // const sendDataToGTM = useGTMDispatch();
    // const location = useLocation();

    // useEffect(() => {
    //   sendDataToGTM({ event: "pageview", value: location });
    // }, [location, sendDataToGTM]);

    const { congress } = useCongress();
    const { user } = useUserMe();

    if (congress.enableFeatureLearn) {
      return (
        <Switch>
          <Route path="/learn">
            <LearnPage />
          </Route>
          <PrivateRoute path="/favourites" availableUnpublished>
            <FavouritesPage />
          </PrivateRoute>
          {/* MyBadge Feature Routes */}
          <FeatureRoute featureName="my_badge" path="/my-badge">
            <PrivateRoute>
              <MyBadgePage />
            </PrivateRoute>
          </FeatureRoute>
          <FeatureRoute featureName="my_badge" exact path="/physical_rooms/:id">
            <PrivateRoute roles={["assistant"]}>
              <PhysicalRoomPage />
            </PrivateRoute>
          </FeatureRoute>
          <FeatureRoute featureName="my_badge" exact path="/physical_rooms">
            <PrivateRoute roles={["assistant"]}>
              <PhysicalRoomsPage />
            </PrivateRoute>
          </FeatureRoute>
          <FeatureRoute featureName="my_badge" exact path="/physical_rooms/:roomId/qr-scan">
            <PrivateRoute roles={["assistant"]}>
              <QrScanPage />
            </PrivateRoute>
          </FeatureRoute>

          {/* Presentation Feature Routes */}
          <FeatureRoute featureName="presentation" path="/my-presentations">
            <PrivateRoute roles={["speaker"]} availableUnpublished>
              <MyPresentationsPage />
            </PrivateRoute>
          </FeatureRoute>

          {/* Public Routes */}
          <Route exact path="/forgot-password">
            <ForgotPasswordPage />
          </Route>
          <Route exact path="/reset-password">
            <ResetPasswordPage />
          </Route>
          <Route exact path="/access-code">
            <AccessCodePage />
          </Route>
          <Route exact path="/register">
            <RegisterPage />
          </Route>
          <Route exact path="/login">
            <LoginPage />
          </Route>
          <PrivateRoute path="/profile" availableUnpublished>
            <ProfilePage />
          </PrivateRoute>
          <PrivateRoute path="/help-desk" availableUnpublished>
            <HelpDeskPage />
          </PrivateRoute>
          <PrivateRoute path="/info" availableUnpublished>
            <InfoPage />
          </PrivateRoute>
          {/* Default redirect to Profile */}
          {user.loaded ? <Redirect to="/learn" /> : <Redirect to="/login" />}
        </Switch>
      );
    }

    if (congress.closed) {
      return (
        <Switch>
          <Route path="/thank-you">
            <ThankYouPage />
          </Route>
          <Redirect to="/thank-you" />
        </Switch>
      );
    }

    return (
      <Switch>
        {/* Private Routes */}
        <PrivateRoute path="/home">
          <HomePage />
        </PrivateRoute>
        <PrivateRoute path="/profile" availableUnpublished>
          <ProfilePage />
        </PrivateRoute>
        <PrivateRoute path="/agenda" availableUnpublished>
          <AgendaPage />
        </PrivateRoute>
        <PrivateRoute path="/favourites" availableUnpublished>
          <FavouritesPage />
        </PrivateRoute>
        <PrivateRoute path="/help-desk" availableUnpublished>
          <HelpDeskPage />
        </PrivateRoute>
        <PrivateRoute path="/meetings/:roomName">
          <MeetingPage />
        </PrivateRoute>
        <PrivateRoute path="/terms" availableUnpublished>
          <TermsPage />
        </PrivateRoute>
        <PrivateRoute path="/info" availableUnpublished>
          <InfoPage />
        </PrivateRoute>

        {/* MyBadge Feature Routes */}
        <FeatureRoute featureName="my_badge" path="/my-badge">
          <PrivateRoute>
            <MyBadgePage />
          </PrivateRoute>
        </FeatureRoute>
        <FeatureRoute featureName="my_badge" exact path="/physical_rooms/:id">
          <PrivateRoute roles={["assistant"]}>
            <PhysicalRoomPage />
          </PrivateRoute>
        </FeatureRoute>
        <FeatureRoute featureName="my_badge" exact path="/physical_rooms">
          <PrivateRoute roles={["assistant"]}>
            <PhysicalRoomsPage />
          </PrivateRoute>
        </FeatureRoute>

        <FeatureRoute featureName="my_badge" exact path="/physical_rooms/:roomId/qr-scan">
          <PrivateRoute roles={["assistant"]}>
            <QrScanPage />
          </PrivateRoute>
        </FeatureRoute>

        {/* Program Feature Routes */}
        <FeatureRoute featureName="program" path="/program">
          <PrivateRoute>
            <ProgramPage />
          </PrivateRoute>
        </FeatureRoute>

        {/* E-Posters Feature Routes */}
        <FeatureRoute featureName="eposters" path="/e-posters/:id">
          <PrivateRoute availableUnpublished>
            <EPosterPage />
          </PrivateRoute>
        </FeatureRoute>
        <FeatureRoute featureName="eposters" path="/e-posters">
          <PrivateRoute path="/e-posters">
            <EPostersPage />
          </PrivateRoute>
        </FeatureRoute>

        <FeatureRoute featureName="eposters" path="/my-posters">
          <PrivateRoute roles={["accademic"]} availableUnpublished>
            <MyPostersPage />
          </PrivateRoute>
        </FeatureRoute>

        {/* Presentations Feature Routes */}
        <FeatureRoute featureName="presentation" path="/my-presentations">
          <PrivateRoute roles={["speaker"]} availableUnpublished>
            <MyPresentationsPage />
          </PrivateRoute>
        </FeatureRoute>

        {/* Sessions Feature Routes */}
        <FeatureRoute featureName="sessions" path="/sessions/:id">
          <PrivateRoute>
            <SessionPage />
          </PrivateRoute>
        </FeatureRoute>
        <FeatureRoute featureName="sessions" path="/sessions">
          <PrivateRoute>
            <SessionsPage />
          </PrivateRoute>
        </FeatureRoute>

        {/* Exhibitors Feature Routes */}
        <FeatureRoute featureName="companies" path="/companies/:id">
          <PrivateRoute path="/companies/:id">
            <ExhibitorPage />
          </PrivateRoute>
        </FeatureRoute>
        <FeatureRoute featureName="companies" path="/companies">
          <PrivateRoute>
            <ExhibitorsPage />
          </PrivateRoute>
        </FeatureRoute>

        <FeatureRoute featureName="companies" path="/meeting-scheduler">
          <PrivateRoute roles={["expert", "exhibitor"]} availableUnpublished>
            <MeetingSchedulerPage />
          </PrivateRoute>
        </FeatureRoute>

        {/* Expert Feature Routes */}
        <FeatureRoute featureName="experts" path="/experts/:id">
          <PrivateRoute>
            <ExpertPage />
          </PrivateRoute>
        </FeatureRoute>
        <FeatureRoute featureName="experts" path="/experts">
          <PrivateRoute>
            <ExpertsPage />
          </PrivateRoute>
        </FeatureRoute>

        {/* Forum Feature Routes */}
        <FeatureRoute featureName="forum" path="/forum">
          <PrivateRoute>
            <ForumPage />
          </PrivateRoute>
        </FeatureRoute>
        <FeatureRoute featureName="forum" path="/forum-topic/:id">
          <PrivateRoute>
            <ForumTopicPage />
          </PrivateRoute>
        </FeatureRoute>

        {/* Social Wall */}
        <FeatureRoute featureName="socialwall" path="/social-wall">
          <PrivateRoute>
            <SocialWallPage />
          </PrivateRoute>
        </FeatureRoute>

        {/* Public Routes */}
        <Route exact path="/forgot-password">
          <ForgotPasswordPage />
        </Route>
        <Route exact path="/reset-password">
          <ResetPasswordPage />
        </Route>
        <Route exact path="/access-code">
          <AccessCodePage />
        </Route>
        <Route exact path="/register">
          <RegisterPage />
        </Route>
        <Route exact path="/login">
          <LoginPage />
        </Route>
        <Route path="/count-down">
          <CountDownPage />
        </Route>

        {/* Default redirect to Profile */}
        {user.loaded ? <Redirect to="/home" /> : <Redirect to="/login" />}
      </Switch>
    );
  };

  return (
    <ReduxProvider store={store}>
      <GoogleTagsProvider>
        <RouterInstance history={history}>
          <DataLoader>
            <ConfigThemeProvider>
              <IntlProvider locale={language} key={language} messages={translations[language]}>
                <SnackbarProvider maxSnack={3}>
                  <FeatureProvider>
                    <Routes />
                  </FeatureProvider>
                </SnackbarProvider>
              </IntlProvider>
            </ConfigThemeProvider>
          </DataLoader>
        </RouterInstance>
      </GoogleTagsProvider>
    </ReduxProvider>
  );
}

export default App;
